import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { signInWithGoogle, linkGoogleToExistingAccount, isValidRedirectRoute } from '../../services/authService';

export default function LoginForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingRedirect, setPendingRedirect] = useState(null);
  const [showLinkingFlow, setShowLinkingFlow] = useState(false);
  const [linkingEmail, setLinkingEmail] = useState('');
  const [linkingPassword, setLinkingPassword] = useState('');
  const [pendingGoogleCredential, setPendingGoogleCredential] = useState(null);

  // Get the redirect path with priority: query param > location state > default to /library
  const getRedirectPath = () => {
    // First, check for 'from' query parameter
    const params = new URLSearchParams(location.search);
    const fromParam = params.get('from');
    
    console.log('[LoginForm] Query param from:', fromParam);
    
    if (fromParam && isValidRedirectRoute(fromParam)) {
      console.log('[LoginForm] Using from query param:', fromParam);
      return fromParam;
    }
    
    // Fall back to location.state (from ProtectedRoute or previous navigation)
    if (location.state?.from?.pathname && isValidRedirectRoute(location.state.from.pathname)) {
      console.log('[LoginForm] Using from location.state.pathname:', location.state.from.pathname);
      return location.state.from.pathname;
    }
    if (location.state?.from && typeof location.state.from === 'string' && isValidRedirectRoute(location.state.from)) {
      console.log('[LoginForm] Using from location.state:', location.state.from);
      return location.state.from;
    }
    
    // Default to library
    console.log('[LoginForm] No valid from found, using /library');
    return '/library';
  };

  // Handle navigation after auth state is confirmed
  useEffect(() => {
    console.log('[LoginForm] useEffect - user:', !!user, 'pendingRedirect:', pendingRedirect);
    if (user && pendingRedirect) {
      console.log('[LoginForm] Navigating to pendingRedirect:', pendingRedirect);
      navigate(pendingRedirect, { replace: true });
      setPendingRedirect(null);
    }
  }, [user, pendingRedirect, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      const redirectPath = getRedirectPath();
      console.log('[LoginForm] After login, redirectPath:', redirectPath);
      setPendingRedirect(redirectPath);
    } catch (err) {
      setError('Invalid email or password');
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      const result = await signInWithGoogle();
      
      if (result.error === 'account-exists-with-different-credential') {
        // Email already exists with password provider
        setLinkingEmail(result.email);
        setPendingGoogleCredential(result.pendingCredential);
        setShowLinkingFlow(true);
        setLoading(false);
      } else if (result.error) {
        // Silently reset loading for user cancellations, show error for real failures
        if (result.error.includes('closed') || result.error.includes('cancelled') || result.error.includes('blocked')) {
          setLoading(false);
        } else {
          setError(result.error);
          setLoading(false);
        }
      } else {
        // Google sign-in successful
        const redirectPath = getRedirectPath();
        console.log('[LoginForm] After Google signin, redirectPath:', redirectPath);
        setPendingRedirect(redirectPath);
      }
    } catch (err) {
      setError('Failed to sign in with Google');
      setLoading(false);
    }
  };

  const handleLinkAccounts = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await linkGoogleToExistingAccount(
        linkingEmail,
        linkingPassword,
        pendingGoogleCredential
      );

      if (result.error) {
        setError(result.error);
        setLoading(false);
      } else {
        // Linking successful
        setShowLinkingFlow(false);
        const redirectPath = getRedirectPath();
        console.log('[LoginForm] After account linking, redirectPath:', redirectPath);
        setPendingRedirect(redirectPath);
      }
    } catch (err) {
      setError('Failed to link accounts');
      setLoading(false);
    }
  };

  if (showLinkingFlow) {
    return (
      <div className="mt-2 p-3 min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50 p-4">
        <div className="w-full max-w-md">
          <form onSubmit={handleLinkAccounts} className="bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Link Accounts</h2>
            <p className="text-gray-600 text-sm mb-6">
              An account already exists for <span className="font-medium">{linkingEmail}</span>. 
              Enter your password to link your Google account.
            </p>
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div className="mb-6">
              <label htmlFor="linkingPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <svg className="absolute left-3 top-3 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <input
                  type="password"
                  id="linkingPassword"
                  value={linkingPassword}
                  onChange={(e) => setLinkingPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 text-white py-2.5 px-4 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 font-medium mb-3"
            >
              {loading ? 'Linking...' : 'Link Accounts'}
            </button>

            <button
              type="button"
              onClick={() => {
                setShowLinkingFlow(false);
                setError('');
                setLinkingPassword('');
              }}
              className="w-full bg-gray-100 text-gray-800 py-2.5 px-4 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              Cancel
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2 flex items-center justify-center bg-white">
      <div className="w-full max-w-2xl p-5">
        <div className="space-y-2">
          <div className="mb-2">
            <h1 className="text-xl font-bold text-gray-900">LOGIN</h1>
            <p className="text-gray-600 text-xs">Sign in to your account</p>
          </div>

          {error && (
            <div className="p-1.5 bg-red-50 border border-red-200 text-red-700 rounded text-xs mb-2">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-2">
            <div>
              <label htmlFor="email" className="block text-xs font-medium text-gray-700 mb-0.5">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="you@example.com"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-xs font-medium text-gray-700 mb-0.5">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="••••••••"
                required
              />
            </div>

            <div className="flex justify-end">
              <Link 
                to="/forgot-password"
                className="text-xs text-indigo-600 hover:text-indigo-700"
              >
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 text-white py-1.5 rounded hover:bg-indigo-700 transition-colors disabled:opacity-50 font-medium text-sm"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>

          <div className="relative py-1">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="px-2 bg-white text-gray-600 text-xs">Or</span>
            </div>
          </div>

          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="w-full bg-white text-gray-800 py-1.5 rounded border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 font-medium text-sm"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {loading ? 'Signing in...' : 'Google'}
          </button>

           <p className="text-center text-xs text-gray-600">
             Don't have an account?{' '}
             <Link 
               to={`/register${location.search}`}
               className="text-indigo-600 hover:text-indigo-700 font-medium"
             >
               Sign up
             </Link>
           </p>
        </div>
      </div>
    </div>
  );
}